import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  AJOUTER_PARAMETRE,
  LISTE_PARAMETRE_CONFIG,
} from "../../Utils/constant";
import { store } from "../../services/service";
import { useConfigStore } from "../../store/ConfigStore";

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconCheck = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconLoader = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="animate-spin"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);
const IconWarning = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconSettings = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// ─── Config definition ────────────────────────────────────────────────────────
const buildConfig = (data) => [
  {
    section: "Numériques",
    items: [
      {
        id: 1,
        description: "Nombre d'opérations par équipe",
        param: "TOTALE_OPERATION",
        defaultValue: data?.TOTALE_OPERATION,
        message: "Distance de localisation appliquée",
        type: "number",
      },
      {
        id: 2,
        description: "Distance PBO (kilomètre)",
        param: "DISTANCE_PBO",
        defaultValue: data?.DISTANCE_PBO,
        message: "Distance PBO appliquée",
        type: "number",
      },
    ],
  },
  {
    section: "Booléens",
    items: [
      {
        id: 3,
        description: "Afficher le champ SN lors de la livraison",
        param: "CHAMPS_SN",
        defaultValue: data?.CHAMPS_SN,
        message: "Configuration SN appliquée",
        type: "boolean",
      },
      {
        id: 4,
        description: "Attribution automatique des rendez-vous en attente",
        param: "ATTRIBUTION_AUTOMATIQUE_RDV",
        defaultValue: data?.ATTRIBUTION_AUTOMATIQUE_RDV,
        message: "Configuration appliquée",
        type: "boolean",
      },
      {
        id: 5,
        description: "Afficher le champ numéro d'avantage",
        param: "CHAMPS_NUMERO_AVANTAGE",
        defaultValue: data?.CHAMPS_NUMERO_AVANTAGE,
        message: "Numéro d'avantage appliqué",
        type: "boolean",
      },
      {
        id: 6,
        description: "Afficher le champ OTP",
        param: "CHAMPS_OTP",
        defaultValue: data?.CHAMPS_OTP,
        message: "Configuration OTP appliquée",
        type: "boolean",
      },
      {
        id: 7,
        description: "Validation des ventes par le SP",
        param: "VALIDATION_SP",
        defaultValue: data?.VALIDATION_SP,
        message: "Configuration validation SP appliquée",
        type: "boolean",
        warn: true,
      },
    ],
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
const TypeTag = ({ type }) =>
  type === "number" ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600">
      nombre
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">
      oui / non
    </span>
  );

const SectionLabel = ({ label }) => (
  <div className="flex items-center gap-2 px-5 py-2.5 bg-gray-50 border-b border-gray-100">
    <IconSettings />
    <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
      {label}
    </span>
  </div>
);

const ConfigRow = ({
  item,
  value,
  onChange,
  onApply,
  isLoading,
  isAnyLoading,
}) => {
  const isBool = item.type === "boolean";
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 transition-colors duration-100">
      {/* Label */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 leading-snug">
          {item.description}
        </p>
        <code className="text-[11px] text-gray-400 mt-0.5 block font-mono">
          {item.param}
        </code>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 shrink-0">
        <TypeTag type={item.type} />

        {isBool ? (
          <select
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="h-8 w-24 text-center border border-gray-200 rounded-lg text-xs font-medium text-gray-800 bg-white outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition cursor-pointer"
          >
            <option value="">--</option>
            <option value="true">OUI</option>
            <option value="false">NON</option>
          </select>
        ) : (
          <input
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            type="number"
            className="h-8 w-24 text-center border border-gray-200 rounded-lg text-xs font-medium text-gray-800 bg-white outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition"
          />
        )}

        <button
          onClick={onApply}
          disabled={isAnyLoading}
          className={`h-8 px-3.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all duration-150 ${
            isLoading
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : isAnyLoading
                ? "bg-yellow-200 text-yellow-500 cursor-not-allowed"
                : "bg-yellow-400 hover:bg-yellow-300 active:scale-95 text-gray-900 cursor-pointer"
          }`}
        >
          {isLoading ? (
            <>
              <IconLoader />
              <span>Envoi…</span>
            </>
          ) : (
            <>
              <IconCheck />
              <span>Appliquer</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const ConfirmModal = ({ open, message, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-in fade-in zoom-in-95 duration-150">
        <div className="flex items-start gap-3 mb-4">
          <span className="mt-0.5 text-amber-500 shrink-0">
            <IconWarning />
          </span>
          <h3 className="font-mtn text-base font-semibold text-gray-900 leading-snug">
            Confirmer la modification
          </h3>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5">
          {message}
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="h-9 px-4 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="h-9 px-4 rounded-lg text-xs font-semibold bg-yellow-400 hover:bg-yellow-300 text-gray-900 transition active:scale-95"
          >
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export const Configuration = () => {
  const param = useConfigStore();
  const [submitting, setSubmitting] = useState(null);
  const [values, setValues] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [pendingItem, setPendingItem] = useState(null);
  const [pendingValue, setPendingValue] = useState("");

  useEffect(() => {
    param.getData(LISTE_PARAMETRE_CONFIG);
  }, []);

  useEffect(() => {
    if (param?.data) {
      const toStr = (v) => (v !== undefined && v !== null ? String(v) : "");
      setValues({
        TOTALE_OPERATION: param.data?.TOTALE_OPERATION ?? "",
        DISTANCE_PBO: param.data?.DISTANCE_PBO ?? "",
        CHAMPS_SN: toStr(param.data?.CHAMPS_SN),
        ATTRIBUTION_AUTOMATIQUE_RDV: toStr(
          param.data?.ATTRIBUTION_AUTOMATIQUE_RDV,
        ),
        CHAMPS_NUMERO_AVANTAGE: toStr(param.data?.CHAMPS_NUMERO_AVANTAGE),
        VALIDATION_SP: toStr(param.data?.VALIDATION_SP),
        CHAMPS_OTP: toStr(param.data?.CHAMPS_OTP),
      });
    }
  }, [param?.data]);

  const handleApply = (item, overrideValue) => {
    const valeur = overrideValue ?? values[item.param] ?? "";
    setSubmitting(item.param);
    store(`${AJOUTER_PARAMETRE}`, "application/json", {
      designation: item.param,
      valeur,
    })
      .then((res) => {
        if (res.status === 200) {
          toast.success(item.message);
          param.getData(LISTE_PARAMETRE_CONFIG);
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.donnee || "Erreur lors de l'enregistrement",
        );
      })
      .finally(() => setSubmitting(null));
  };

  const requestApply = (item) => {
    const valeur = values[item.param] ?? "";
    if (item.warn && valeur === "false") {
      setPendingItem(item);
      setPendingValue(valeur);
      setModalMessage(
        "Vous êtes sur le point de désactiver la validation des ventes par le SP. Cette action va entraîner la migration des ventes à l'étape en attente de survey.",
      );
      setShowModal(true);
      return;
    }
    handleApply(item, valeur);
  };

  const confirmApply = () => {
    if (!pendingItem) return;
    handleApply(pendingItem, pendingValue);
    setPendingItem(null);
    setPendingValue("");
    setShowModal(false);
  };

  const cancelApply = () => {
    setPendingItem(null);
    setPendingValue("");
    setShowModal(false);
  };

  const sections = buildConfig(param?.data);
  const totalParams = sections.reduce((acc, s) => acc + s.items.length, 0);

  return (
    <div className="mt-4 max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <p className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
          Configuration
        </p>
        <p className="text-sm text-gray-400 mb-3">
          Paramètres système de l'application
        </p>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-3 py-1">
          <IconSettings />
          {totalParams} paramètres configurables
        </span>
      </div>

      {/* Panel */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-100">
        {sections.map((section) => (
          <div key={section.section}>
            <SectionLabel label={section.section} />
            {section.items.map((item) => (
              <ConfigRow
                key={item.id}
                item={item}
                value={values[item.param]}
                onChange={(val) =>
                  setValues((s) => ({ ...s, [item.param]: val }))
                }
                onApply={() => requestApply(item)}
                isLoading={submitting === item.param}
                isAnyLoading={!!submitting}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Confirmation modal */}
      <ConfirmModal
        open={showModal}
        message={modalMessage}
        onConfirm={confirmApply}
        onCancel={cancelApply}
      />
    </div>
  );
};
